import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { ProductGroupFormValues } from "../productGroupTypes"
import {
    GetProductGroupDetailsResponse,
    getProductGroupDetails,
    updateProductGroup,
    ProductGroupQuery,
} from "../productGroupApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"
import { useNavigate, useParams } from "react-router-dom"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useDateTime } from "../../../../localization/useDateTime"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { prepareProductGroup } from "../helpers/prepareProductGroup"
import { prepareInitialValues } from "../helpers/prepareInitialValues"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import React, { forwardRef, Suspense, useRef } from "react"
import { FormikProps } from "formik"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateProductGroupForm } from "../components/CreateUpdateProductGroupForm"

function transformData(data: GetProductGroupDetailsResponse) {
    return data.productGroup
}

export function EditProductGroup() {
    const formRef = useRef<FormikProps<ProductGroupFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="product-group-page-title-update" attrs={{ title: true }}>
                <PageHeader
                    title="Edit product group"
                    page={Page.MERCHANT_PRODUCT_GROUP}
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    rightElement={
                        <LocalizedStrict id="create-product-group-save-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-groups">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE}>Product groups</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-product-group-update">
                            <Breadcrumb>Update product group</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ProductGroup ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const ProductGroup = forwardRef<FormikProps<ProductGroupFormValues>>((props, ref) => {
    const navigate = useNavigate()

    const { merchantId } = useMerchant()
    const dateTimeFactory = useDateTime()

    const { productGroupId } = useParams()
    const productGroupIdNumber = Number(productGroupId)

    const { mutate: callUpdateProductGroup } = useApiMutation(updateProductGroup, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            navigate(MERCHANT_ADMIN_PRODUCTS_GROUP_ROUTE)

            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const { data: productGroup } = useApiQuery(getProductGroupDetails, {
        queryName: ProductGroupQuery.DETAILS,
        dependencies: {
            merchantId,
            productGroupId: productGroupIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: productGroupId !== undefined,
        select: transformData,
    })

    async function handleEditProductGroup(values: ProductGroupFormValues) {
        const productGroup = prepareProductGroup(values, dateTimeFactory)

        callUpdateProductGroup({
            merchantId,
            productGroupId: productGroupIdNumber,
            productGroup,
        })
    }

    return (
        <CreateUpdateProductGroupForm
            ref={ref}
            history={productGroup?.history}
            initialValues={prepareInitialValues(dateTimeFactory, productGroup)}
            onSubmit={handleEditProductGroup}
        />
    )
})
